<template>
  <header class="sf-cover">
    <div class="sf-cover__banner" :style="bannerStyle" role="img" :aria-label="`Capa de ${restaurant?.name || 'loja'}`"></div>

    <div class="sf-cover__info">
      <div class="sf-cover__logo">
        <img v-if="logo" :src="logo" :alt="restaurant?.name || 'logo'" @error="logoFailed = true">
        <span v-else-if="restaurant?.name">{{ restaurant.name.trim().charAt(0).toUpperCase() }}</span>
        <span v-else class="sf-cover__logo-skeleton sf-skeleton animate-pulse"></span>
      </div>

      <div class="sf-cover__text">
        <h1 v-if="restaurant?.name" class="sf-heading sf-cover__name">{{ restaurant.name }}</h1>
        <h1 v-else class="sf-cover__name-skeleton sf-skeleton animate-pulse"></h1>

        <p v-if="status || minimumOrder" class="sf-cover__status">
          <span v-if="status" class="sf-cover__badge" :class="status.open ? 'is-open' : 'is-closed'">
            <span class="sf-cover__dot"></span>{{ status.label }}
          </span>
          <span v-if="minimumOrder" class="sf-cover__min">Pedido mínimo {{ minimumOrder }}</span>
        </p>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { restaurant } from '@/global/state/GlobalState'
import { storefront } from '@/global/state/StorefrontState'
import { formatBRL, getStoreStatus, resolveMediaUrl } from './storefrontUtils'

const logoFailed = ref(false)
const logo = computed(() => (logoFailed.value ? '' : restaurant.value?.restaurantDesignCustom?.logo || ''))
watch(() => restaurant.value?.restaurantDesignCustom?.logo, () => { logoFailed.value = false })

const bannerStyle = computed(() => {
  const url = resolveMediaUrl(storefront.value.settings.coverImageUrl)
  if (url) return { backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,.25)), url("${url}")` }
  return { backgroundImage: 'linear-gradient(135deg, var(--sf-primary) 0%, var(--sf-secondary) 140%)' }
})

// Recalculated every minute so "Aberto/Fechado" doesn't go stale on an open tab
const now = ref(new Date())
const clock = setInterval(() => { now.value = new Date() }, 60000)
onBeforeUnmount(() => clearInterval(clock))
const status = computed(() => (restaurant.value?.id ? getStoreStatus(restaurant.value, now.value) : null))

const minimumOrder = computed(() => {
  const value = Number(restaurant.value?.restaurantConfig?.minimumOrderAmount)
  return value > 0 ? formatBRL(value) : ''
})
</script>

<style>
.sf-cover {
  margin-bottom: 1.75rem;
  color: var(--sf-text);
}
.sf-cover__banner {
  height: 9.5rem;
  background-color: var(--sf-primary);
  background-size: cover;
  background-position: center;
}
@media (min-width: 640px) {
  .sf-cover__banner {
    height: 13rem;
    border-radius: var(--sf-radius);
  }
}
@media (min-width: 1024px) {
  .sf-cover__banner {
    height: 16rem;
  }
}
.sf-cover__info {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0 1rem;
}
@media (min-width: 1024px) {
  .sf-cover__info {
    padding: 0 2rem;
    gap: 1.25rem;
  }
}
.sf-cover__logo {
  flex-shrink: 0;
  width: 5.5rem;
  height: 5.5rem;
  margin-top: -2.75rem;
  position: relative;
  border-radius: 999px;
  border: 4px solid var(--sf-bg);
  background: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, .15);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--sf-heading-font);
  font-size: 2rem;
  font-weight: 700;
  color: var(--sf-primary);
}
@media (min-width: 1024px) {
  .sf-cover__logo {
    width: 7.5rem;
    height: 7.5rem;
    margin-top: -3.75rem;
  }
}
.sf-cover__logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: .5rem;
}
.sf-cover__logo-skeleton {
  width: 100%;
  height: 100%;
}
.sf-cover__text {
  min-width: 0;
  padding-top: .6rem;
}
.sf-cover__name {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}
@media (min-width: 1024px) {
  .sf-cover__name {
    font-size: 1.75rem;
  }
}
.sf-cover__name-skeleton {
  height: 1.5rem;
  width: 10rem;
  border-radius: 6px;
}
.sf-cover__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .25rem .75rem;
  margin-top: .35rem;
  font-size: .8125rem;
  font-family: var(--sf-font);
  color: var(--sf-muted);
}
.sf-cover__badge {
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  font-weight: 600;
}
.sf-cover__badge.is-open {
  color: #16a34a;
}
.sf-cover__badge.is-closed {
  color: #ef4444;
}
.sf-cover__dot {
  width: .5rem;
  height: .5rem;
  border-radius: 999px;
  background: currentColor;
}
html.sf-dark .sf-cover__badge.is-open {
  color: #4ade80;
}
html.sf-dark .sf-cover__badge.is-closed {
  color: #f87171;
}
</style>
